import { all, takeEvery, put, fork, call, takeLatest, select } from 'redux-saga/effects';
import { push, replace } from 'connected-react-router';
import jwtDecode from 'jwt-decode';
import _ from 'lodash';
import { getToken } from '@helpers/utility';
import kc, { signOut, getUserProfile, loadKc } from '@helpers/keycloak';
import HSBBClient from '@helpers/apiClient';
import HSBBClientV2 from '@helpers/apiClient-v2';
import {
  clearToken,
  startListener,
  stopListener,
  clearKcCallback,
  checkIsTokenExpired,
  updateSessionTimeout
} from '@helpers/utility';

import actions from './actions';

import { initTokenRefreshHandlers } from '@helpers/tokenManager';

const getUseSettingInfo = (state) => state.Auth.userSetting;

export function* parseKc() {
  try {
    // Check if there's a code in the URL (either in search params or hash fragment)
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const hasCode = urlParams.has('code') || hashParams.has('code');
    
    // If there's no code and Keycloak is not authenticated, don't proceed
    if (!hasCode && (!kc || !kc.authenticated)) {
      console.log("No authentication code found in URL and not authenticated, skipping callback processing");
      return;
    }
    
    // Initialize Keycloak
    yield call(loadKc);
    // If Keycloak is authenticated, get the user profile
    if (kc.authenticated) {
      const userProfile = yield call(getUserProfile);
      
      // Initialize token refresh handlers
      initTokenRefreshHandlers();
      
      yield put({
        type: actions.LOGIN_SUCCESS,
        accessToken: kc.token,
        profile: userProfile
      });
    } else {
      console.log("Keycloak authentication failed");
      yield put({
        type: actions.LOGIN_ERROR,
        error: { error: "Authentication Failed", errorDescription: "Could not authenticate with the server" }
      });
    }
  } catch (error) {
    yield put({
      type: actions.LOGIN_ERROR,
      error
    });
  }
}

export function* handleAuthenticationCallback() {
  yield takeLatest(actions.HANDLE_AUTHENTICATION_CALLBACK, parseKc);
}

export function* login() {
  yield takeEvery(actions.LOGIN, function* () {
    const token = getToken();
    const accessToken = token.get('accessToken');

    if (accessToken) {
      yield put(actions.checkAuthorization());
      return;
    }

    yield call(loadKc);
    kc.login({
      redirectUri: process.env.REACT_APP_KEYCLOAK_CALLBACK_URL
    });
  });
}

export function* fetchUserRole(action) {
  try {
    // const userData = yield call(HSBBClientV2.getUser, action.userId);
    // const userDataPayload = {
    //   userId: userData.data.userId,
    //   roles: userData.data.roles,
    //   celcomStoreId: userData.data.celcomStoreId,
    //   pulseUserId: userData.data.pulseUserId
    // };

    const userSettingInfo = yield select(getUseSettingInfo)
     const userDataPayload = {
      userId: userSettingInfo.userId,
      roles: userSettingInfo.roles,
      celcomStoreId: userSettingInfo.celcom_store_id,
      pulseUserId: userSettingInfo.pulse_user_id,
      moli: userSettingInfo.moli
    };

    window.localStorage.setItem('isOnHold', userSettingInfo.enableCelcomRescheduleOnHold);
    window.localStorage.setItem('isRescheduleEnabled', userSettingInfo.enableReschedule);
    window.localStorage.setItem('enableOpenOrderRestriction', userSettingInfo.enableOpenOrderRestriction);
    
    
    const userRole = yield call(HSBBClientV2.getUserRoles, userDataPayload);

    yield put({
      type: actions.USER_ROLE_LOADED,
      userRole: !_.isEmpty(userRole.data.rolePermissions) ? userRole.data.rolePermissions : null,
      rolePosition: userRole.data.rolePosition !== '' ? userRole.data.rolePosition : '',
      isUserAuthorized:
        userSettingInfo.roles.length > 0 && userSettingInfo.roles.indexOf('Alternate Channel') !== -1
          ? userRole.data.isUserAuthorized
          : true,
      userRoleError: userRole.data.rolePermissionErrors.length > 0 ? userRole.data.rolePermissionErrors : null
    });
  } catch (error) {
    yield put({
      type: actions.USER_ROLE_ERROR,
      error
    });
  }
}

export function* userRole() {
  yield takeLatest(actions.USER_ROLE, fetchUserRole);
}

export function* loginSuccess() {
  yield takeEvery(actions.LOGIN_SUCCESS, function* (payload) {
    yield call(loadKc);
    
    const userSettingData = yield call(HSBBClient.getUserSetting);

    // To get user details from latest dms api
    const userData = yield call(HSBBClientV2.getUser, userSettingData.userId);
    const userSetting = userData?.data;
    userSetting.celcom_store_id = userData?.data.celcomStoreId;
    userSetting.pulse_user_id = userData?.data.pulseUserId;
    userSetting.digi_user_id = userData?.data.storeId;
    userSetting.cd_dealer_id = userData?.data.cdDealerCode;
    delete userSetting.celcomStoreId;
    delete userSetting.pulseUserId;

    window.localStorage.setItem('pulseId', userSetting.pulse_user_id);

    const userDataPayload = {
      userId: userSetting.userId,
      roles: userSetting.roles,
      celcomStoreId: userSetting.celcom_store_id,
      pulseUserId: userSetting.pulse_user_id,
      moli: userSetting.moli,
    };
    const userRole = yield call(HSBBClientV2.getUserRoles, userDataPayload);
    if (
      userSetting.roles.length > 0 &&
      userSetting.roles.indexOf('Alternate Channel') !== -1 &&
      !userRole.data.isUserAuthorized
    ) {
      window.localStorage.setItem('isAuthorizedUser', userRole.data.isUserAuthorized);
      window.localStorage.setItem('unauthorizedError', userRole.data.rolePermissionErrors);
    } else {
      window.localStorage.setItem('isAuthorizedUser', true);
      window.localStorage.setItem('unauthorizedError', '');
    }

    try {
      const updateResponse = yield call(HSBBClientV2.updateUserFromDMS, userSetting.userId, {
        celcomStoreId: userSetting.celcom_store_id,
        storeId: userSetting.digi_user_id,
        cdDealerCode: userSetting.cd_dealer_id,
        source: 'sync'
      });
    } catch (error) {
      console.error('Error updating user:', error);
    }

    const profile = payload.profile;

    yield put({
      type: actions.USER_PROFILE_LOADED,
      profile,
      userSetting
    });
    // Load common config from API
    try {
      const configResponse = yield call(HSBBClient.getSetting, 'COMMON_CONFIG');
      // Store entire commonConfig - API returns the value object directly
      yield put({
        type: actions.COMMON_CONFIG_LOADED,
        payload: configResponse || {}
      });
    } catch (error) {
      console.error('Error loading common config:', error);
    }
    startListener();

    if (!payload.noRedirect) {
      yield put(push('/admin'));
    }
  });
}

export function* loginError() {
  yield takeEvery(actions.LOGIN_ERROR, function (payload) {
    stopListener();
    clearKcCallback();
    clearToken();
    console.log(payload.error);
  });
}

export function* logout() {
  yield takeEvery(actions.LOGOUT, function* () {
    stopListener();
    clearToken();

    yield call(signOut);
  });
}

export function* redirect() {
  yield takeEvery(actions.REDIRECT, function* (payload) {
    yield put(replace(payload.link));
  });
}

export function* redirectToLogin() {
  yield takeEvery(actions.REDIRECT_TO_LOGIN, function* () {
    stopListener();
    clearToken();
    yield put(actions.redirect(process.env.REACT_APP_BASENAME));
  });
}

export function* checkAuthorization() {
  yield takeEvery(actions.CHECK_AUTHORIZATION, function* () {
    try {
      // Initialize Keycloak first
      yield call(loadKc);
      
      // Get tokens using our token utility
      const token = getToken();
      const accessToken = token.get('accessToken');
      const idToken = token.get('idToken');
      const refreshToken = token.get('refreshToken');

      if (!accessToken || !kc.authenticated) {
        yield put(actions.redirectToLogin());
        return;
      }

      const sessionTimeout = window.localStorage.getItem('session_timeout');
      const now = new Date().getTime() / 1000;

      const isActive = !!sessionTimeout && sessionTimeout > now;
      const isAccessExpired = checkIsTokenExpired(accessToken);
      const isRefreshExpired = checkIsTokenExpired(refreshToken);
      const decodedAccessToken = jwtDecode(accessToken);

      const notValid =
        decodedAccessToken.iss !==
        process.env.REACT_APP_KEYCLOAK_AUTH_SERVER_URL + '/realms/' + process.env.REACT_APP_KEYCLOAK_REALM;

      if (notValid || isRefreshExpired) {
        yield put(actions.redirectToLogin());
        return;
      }

      if (isAccessExpired && !isActive) {
        yield put(actions.redirectToLogin());
        return;
      }

      if (isActive) {
        updateSessionTimeout();
      }

      yield call(loadKc);
      const profile = jwtDecode(idToken);

      yield put({
        type: actions.LOGIN_SUCCESS,
        accessToken,
        profile,
        appStatus: 'ready',
        noRedirect: true
      });
      // Check alternate channel user has access
      let isAuthorizedUser = window.localStorage.getItem('isAuthorizedUser');
      if (isAuthorizedUser && isAuthorizedUser === 'false') {
        yield put(push('/admin'));
      }
    } catch (e) {
      yield put(actions.redirectToLogin());
    }
  });
}

export default function* rootSaga() {
  yield all([
    fork(checkAuthorization),
    fork(handleAuthenticationCallback),
    fork(loginSuccess),
    fork(loginError),
    fork(login),
    fork(userRole),
    fork(logout),
    fork(redirect),
    fork(redirectToLogin)
  ]);
}